/**
 * Encloses all types used in Manifest flow.
 *
 * @version 1.0 20 July 2024
 * @author pTalan
 */
import { ExpectedFileColumn } from "../../app/models/requestModels/fileExpectedColumn";
import {
  FilterableColumns,
  FilterableColumnsState
} from "../../components/common/filter/GridDataFilter";

export interface StoreManifestArgs {
  storePrimaryId: number;
  pageIndex: number;
  pageSize: number;
  startDate: string;
  endDate: string;
  filters?: FilterableColumnsState;
}

export const storeGridFilter: FilterableColumns = {
  storeId: {
    displayName: "Store ID",
    options: [],
    type: "string",
  },
  clientStoreId: {
    displayName: "Client Store ID",
    options: [],
    type: "number",
  },
  clientName: {
    displayName: "Client Name",
    options: [],
    type: "string",
  },
  clientId: {
    displayName: "Client ID",
    options: [],
    type: "string",
  },
  location: {
    displayName: "Location",
    options: [],
    type: "string",
  },
  timezone: {
    displayName: "Timezone",
    options: [],
    type: "string",
  }
};

export enum STORE_MANIFEST_COLUMN_NAME {
  ID = "id",
  DRIVER_USER_ID = "driverUserId",
  ORDER_ID = "orderId",
  PO_NUMBER = "poNumber",
  DELIVERY_STATUS = "deliveryStatus",
  TOTE_ID = "toteId",
  ORDER_SEQUENCE_NUMBER = "orderSequenceNumber",
  DROP_NUMBER = "dropNumber",
  IS_LOAD_SCANNED = "isLoadScanned",
  DISPLAY_TRIP_ID = "displayTripId",
  CUST_START_TIME = "custStartTime",
  CUST_END_TIME = "custEndTime",
  STORE_NUMBER = "storeNumber",
  STORE_ID = "storeId",
  FDP_ID = "fdpId",
  FDP_NAME = "fdpName",
  SID = "sid",
  TOTAL_DELIVERY_TASKS = "totalDeliveryTasks",
  TOTAL_PLANNED_MILEAGE = "totalPlannedMileage",
  PLANNED_TRIP_TIME_SEC = "plannedTripTimeSec",
  TRIP_EXTERNAL_ID = "tripExternalId",
  DELIVERY_TASK_UUID = "deliveryTaskUuid",
  CUSTOMER_ADDRESS_LINE_1 = "customerAddressLine1",
  CUSTOMER_CITY = "customerCity",
  CUSTOMER_STATE = "customerState",
  CUSTOMER_POSTALCODE = "customerPostalcode",
  AREA_NAME = "areaName",
  ADDRESS_LINE_1 = "addressLine1",
  CITY = "city",
  STATE = "state",
  POSTAL_CODE = "postalCode",
  TRIP_STATUS = "tripStatus",
  CREATED_BY = "createdBy",
  TIMEZONE = "timezone",
  SHARD = "shard",
  SCHEDULDED_DELIVERY_DATE = "scheduledDeliveryDate",
  MANIFEST_INGESTED_ON = "createdOn",
}

export enum STORE_MANIFEST_LABEL_NAME {
  ID = "ID",
  DRIVER_USER_ID = "Driver User ID",
  ORDER_ID = "Order ID",
  PO_NUMBER = "PO Number",
  DELIVERY_STATUS = "Delivery Status",
  TOTE_ID = "Tote ID",
  ORDER_SEQUENCE_NUMBER = "Order Sequence Number",
  DROP_NUMBER = "Drop Number",
  IS_LOAD_SCANNED = "Load Scanned",
  DISPLAY_TRIP_ID = "Display Trip ID",
  CUST_START_TIME = "Customer Start Time",
  CUST_END_TIME = "Customer End Time",
  STORE_NUMBER = "Store Number",
  STORE_ID = "Store ID",
  FDP_ID = "FDP ID",
  FDP_NAME = "FDP Name",
  SID = "SID",
  TOTAL_DELIVERY_TASKS = "Total Delivery Tasks",
  TOTAL_PLANNED_MILEAGE = "Total Planned Mileage",
  PLANNED_TRIP_TIME_SEC = "Planned Trip Time (Seconds)",
  TRIP_EXTERNAL_ID = "Trip External ID",
  DELIVERY_TASK_UUID = "Delivery Task UUID",
  CUSTOMER_ADDRESS_LINE_1 = "Customer Address Line 1",
  CUSTOMER_CITY = "Customer City",
  CUSTOMER_STATE = "Customer State",
  CUSTOMER_POSTALCODE = "Customer Postal Code",
  AREA_NAME = "Area Name",
  ADDRESS_LINE_1 = "Address Line 1",
  CITY = "City",
  STATE = "State",
  POSTAL_CODE = "Postal Code",
  TRIP_STATUS = "Trip Status",
  CREATED_BY = "Created By",
  TIMEZONE = "Timezone",
  SHARD = "Shard",
  SCHEDULDED_DELIVERY_DATE = "Scheduled Delivery Date",
  MANIFEST_INGESTED_ON = "Manifest Ingested Date",
}

export const StorePkgExpectedColumns: ExpectedFileColumn[] = [
  {
    name: "Driver User ID",
    mandatory: true,
    type: "string",
    camelCaseName: "driverUserId",
  },
  {
    name: "Tote ID",
    mandatory: true,
    type: "string",
    camelCaseName: "toteId",
  },
  {
    name: "Order ID",
    mandatory: true,
    type: "string",
    camelCaseName: "orderId",
  },
  {
    name: "Drop Number",
    mandatory: true,
    type: "number",
    camelCaseName: "dropNumber",
  },
  {
    name: "PO Number",
    mandatory: true,
    type: "string",
    camelCaseName: "poNumber",
  },
  {
    name: "Display Trip ID",
    mandatory: true,
    type: "string",
    camelCaseName: "displayTripId",
  },
  {
    name: "Customer Address Line1",
    mandatory: true,
    type: "string",
    camelCaseName: "customerAddressLine1",
  },
  {
    name: "Customer City",
    mandatory: true,
    type: "string",
    camelCaseName: "customerCity",
  },
  {
    name: "Customer State",
    mandatory: true,
    type: "string",
    camelCaseName: "customerState",
  },
  {
    name: "Customer Postalcode",
    mandatory: true,
    type: "string",
    camelCaseName: "customerPostalCode",
  },
]

export const storeManifestViewGridFilter: FilterableColumns = {
  id: { displayName: "ID", options: [], type: "number"},
  driverUserId: { displayName: "Driver User ID", options: [], type: "string"},
  toteId: { displayName: "Tote ID", options: [], type: "string"},
  orderId: { displayName: "Order ID", options: [], type: "string"},
  displayTripId: {displayName: "Display Trip ID", options: [], type: "string"},
  dropNumber: { displayName: "Drop Number", options: [], type: "string"},
  poNumber: {displayName: "PO Number", options: [], type: "string"},
};

export const storeManifestImportGridFilter: FilterableColumns = {
  id: { displayName: "ID", options: [], type: "number"},
  driverUserId: { displayName: "Driver User ID", options: [], type: "string"},
  toteId: { displayName: "Tote ID", options: [], type: "string"},
  sid: { displayName: "SID", options: [], type: "string"},
  dropNumber: { displayName: "Drop Number", options: [], type: "string"},
  isLoadScanned: { displayName: "Load Scan Status", options: ["True", "False"], type: "boolean"},
  fdpId: { displayName: "FDP ID", options: [], type: "number"},
  fdpName: { displayName: "FDP Name", options: [], type: "string"},
  tripExternalId: { displayName: "Trip External ID", options: [], type: "string"},
  scheduledDeliveryDate: { displayName: "Scheduled Delivery Date", options: [], type: "date"},
};